import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { systemInfoQueryOptions, databaseHealthQueryOptions, apiHealthQueryOptions } from "@/api/systemStatus";

const appVersion = import.meta.env.VITE_APP_VERSION as string | undefined;

const OUTDATED_THRESHOLD_MS = 26 * 60 * 60 * 1000;

type BadgeStatus =
    | "healthy"
    | "success"
    | "unhealthy"
    | "failed"
    | "loading"
    | "unknown"
    | "outdated"
    | "complete"
    | "problem"
    | "etlstatusmismatch";

function StatusBadge({ status }: { status: BadgeStatus }) {
    const configs: Record<BadgeStatus, { color: string; label: string }> = {
        healthy: { color: "var(--color-general-green-to)", label: "Healthy" },
        success: { color: "var(--color-general-green-to)", label: "Success" },
        complete: { color: "var(--color-general-green-to)", label: "Complete" },
        unhealthy: { color: "var(--color-general-red)", label: "Unhealthy" },
        failed: { color: "var(--color-general-red)", label: "Failed" },
        problem: { color: "var(--color-general-red)", label: "Problem" },
        outdated: { color: "#f59e0b", label: "Outdated" },
        etlstatusmismatch: { color: "#f59e0b", label: "ETL Mismatch" },
        loading: { color: "var(--color-rank-silver)", label: "Checking..." },
        unknown: { color: "#94a3b8", label: "Unknown" },
    };
    const cfg = configs[status];
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: `${cfg.color}18`, color: cfg.color }}
        >
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg.color }} />
            {cfg.label}
        </span>
    );
}

function formatDate(iso: string | null | undefined) {
    if (!iso) return "-";
    const utc = iso.endsWith("Z") ? iso : iso + "Z";
    return (
        new Intl.DateTimeFormat("en-GB", {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "UTC",
        }).format(new Date(utc)) + " UTC"
    );
}

function isOutdated(iso: string | null | undefined): boolean {
    if (!iso) return false;
    const utc = iso.endsWith("Z") ? iso : iso + "Z";
    return Date.now() - new Date(utc).getTime() > OUTDATED_THRESHOLD_MS;
}

function InfoRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
    return (
        <div
            className="flex flex-col gap-1 py-3 px-6 md:flex-row md:items-center md:justify-between md:py-3.5"
            style={{ borderBottom: "1px solid rgba(199,197,214,0.15)" }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f6f3f2";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "";
            }}
        >
            <span className="text-xs font-medium text-on-surface-variant uppercase tracking-wide md:text-sm md:normal-case md:tracking-normal">
                {label}
            </span>
            {children ?? <span className="text-sm font-bold text-on-surface break-all">{value}</span>}
        </div>
    );
}

export const SystemStatusPage = () => {
    const [dataExpanded, setDataExpanded] = useState(false);
    const { data: sysInfo, isLoading: isSysLoading, isError: isSysError } = useQuery(systemInfoQueryOptions);
    const { data: dbHealth, isLoading: isDbLoading, isError: isDbError } = useQuery(databaseHealthQueryOptions);
    const { data: apiHealth, isLoading: isApiLoading, isError: isApiError } = useQuery(apiHealthQueryOptions);

    const apiStatus: BadgeStatus = isApiLoading
        ? "loading"
        : isApiError
          ? "unhealthy"
          : apiHealth?.status === "Healthy"
            ? "healthy"
            : "unknown";

    const dbStatus: BadgeStatus = isDbLoading
        ? "loading"
        : isDbError
          ? "unhealthy"
          : dbHealth?.status === "Healthy"
            ? "healthy"
            : "unknown";

    const etlStatus: BadgeStatus = isSysLoading
        ? "loading"
        : isSysError
          ? "unknown"
          : sysInfo?.etlStatus === "failed"
            ? "failed"
            : sysInfo?.etlStatus === "outdated" || isOutdated(sysInfo?.lastEtlRun)
              ? "outdated"
              : sysInfo?.etlStatus === "success"
                ? "success"
                : "unknown";

    const ingestionGeneralStatus: BadgeStatus = isSysLoading
        ? "loading"
        : isSysError
          ? "unknown"
          : sysInfo?.ingestionGeneralStatus === "failed"
            ? "failed"
            : sysInfo?.ingestionGeneralStatus === "etlstatusmismatch"
              ? "etlstatusmismatch"
              : sysInfo?.ingestionGeneralStatus === "outdated" || isOutdated(sysInfo?.lastIngestionGeneralRun)
                ? "outdated"
                : sysInfo?.ingestionGeneralStatus === "success"
                  ? "success"
                  : "unknown";

    const ingestionMatchesStatus: BadgeStatus = isSysLoading
        ? "loading"
        : isSysError
          ? "unknown"
          : sysInfo?.ingestionMatchesStatus === "failed"
            ? "failed"
            : sysInfo?.ingestionMatchesStatus === "etlstatusmismatch"
              ? "etlstatusmismatch"
              : sysInfo?.ingestionMatchesStatus === "outdated" || isOutdated(sysInfo?.lastIngestionMatchesRun)
                ? "outdated"
                : sysInfo?.ingestionMatchesStatus === "success"
                  ? "success"
                  : "unknown";

    const dataStatuses = [etlStatus, ingestionGeneralStatus, ingestionMatchesStatus];
    const dataGroupStatus: BadgeStatus = isSysLoading
        ? "loading"
        : dataStatuses.some((s) => s === "failed")
          ? "problem"
          : dataStatuses.some((s) => s === "etlstatusmismatch")
            ? "problem"
            : dataStatuses.some((s) => s === "outdated")
              ? "outdated"
              : dataStatuses.every((s) => s === "success")
                ? "complete"
                : "unknown";

    const sections = [
        {
            title: "Application",
            color: "#050066",
            rows: [
                {
                    label: "Frontend Version",
                    value: appVersion ?? "development build",
                },
                {
                    label: "API Commit Hash",
                    value: isSysLoading ? "Loading..." : isSysError ? "Unavailable" : (sysInfo?.commitHash ?? "—"),
                },
                {
                    label: "Last ETL Pipeline Run",
                    value: isSysLoading ? "Loading..." : isSysError ? "Unavailable" : formatDate(sysInfo?.lastEtlRun),
                },
                {
                    label: "Last General Ingestion Run",
                    value: isSysLoading
                        ? "Loading..."
                        : isSysError
                          ? "Unavailable"
                          : formatDate(sysInfo?.lastIngestionGeneralRun),
                },
                {
                    label: "Last General Matches Run",
                    value: isSysLoading
                        ? "Loading..."
                        : isSysError
                          ? "Unavailable"
                          : formatDate(sysInfo?.lastIngestionMatchesRun),
                },
                {
                    label: "Status Check At",
                    value: isSysLoading ? "Loading..." : formatDate(sysInfo?.timestamp),
                },
            ],
        },
    ];

    return (
        <main className="flex-1 flex flex-col p-4 min-h-0">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
                <div>
                    <h1 className="text-4xl font-black text-primary tracking-tight mb-2 font-headline">
                        System Status
                    </h1>
                    <p className="text-on-surface-variant font-medium">
                        Live health and version information for Hockeyly services.
                    </p>
                </div>
            </div>

            <div className="flex flex-col gap-4 pb-8 max-w-2xl">
                <div className="bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden">
                    <div
                        className="px-6 py-3.5"
                        style={{ background: "#ebe7e7", borderBottom: "1px solid rgba(199,197,214,0.3)" }}
                    >
                        <span
                            className="font-headline font-black text-[10px] uppercase tracking-widest"
                            style={{ color: "#050066" }}
                        >
                            Application
                        </span>
                    </div>
                    {sections[0].rows.map((row) => (
                        <InfoRow key={row.label} label={row.label} value={row.value} />
                    ))}
                </div>

                <div className="bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden">
                    <div
                        className="px-6 py-3.5"
                        style={{ background: "#ebe7e7", borderBottom: "1px solid rgba(199,197,214,0.3)" }}
                    >
                        <span
                            className="font-headline font-black text-[10px] uppercase tracking-widest"
                            style={{ color: "#050066" }}
                        >
                            Services
                        </span>
                    </div>
                    <InfoRow label="API">
                        <StatusBadge status={apiStatus} />
                    </InfoRow>
                    <InfoRow label="Database">
                        <StatusBadge status={dbStatus} />
                    </InfoRow>
                    <button
                        type="button"
                        className="w-full flex items-center justify-between py-3.5 px-6 text-left"
                        style={{ borderBottom: dataExpanded ? "1px solid rgba(199,197,214,0.15)" : undefined }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f6f3f2";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "";
                        }}
                        onClick={() => setDataExpanded((v) => !v)}
                    >
                        <span className="text-sm font-medium text-on-surface-variant">Data</span>
                        <div className="flex items-center gap-2">
                            <StatusBadge status={dataGroupStatus} />
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                style={{
                                    transition: "transform 0.2s",
                                    transform: dataExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    color: "var(--color-on-surface-variant)",
                                }}
                            >
                                <path
                                    d="M4 6l4 4 4-4"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </button>
                    {dataExpanded && (
                        <>
                            <InfoRow label="ETL Status">
                                <StatusBadge status={etlStatus} />
                            </InfoRow>
                            <InfoRow label="Ingestion General Status">
                                <StatusBadge status={ingestionGeneralStatus} />
                            </InfoRow>
                            <InfoRow label="Ingestion Matches Status">
                                <StatusBadge status={ingestionMatchesStatus} />
                            </InfoRow>
                        </>
                    )}
                </div>
            </div>
            <div className="md:hidden py-6" />
        </main>
    );
};
